import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  CheckCircle,
  AlertTriangle,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Users,
  Timer,
  Award,
  Zap,
  Target as TargetIcon,
  DollarSign as DollarIcon
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Analytics Chart Component
const AnalyticsChart = ({ title, data, type = 'bar' }) => {
  const renderChart = () => {
    if (type === 'line') {
      return <LineChart data={data} />;
    } else if (type === 'pie') {
      return <CustomPieChart data={data} />;
    } else {
      return <BarChart data={data} />;
    }
  };

  return (
    <div className="card h-100">
      <div className="card-body">
        <h6 className="card-title d-flex align-items-center">
          <BarChart3 size={18} className="me-2" style={{ color: 'var(--primary-color)' }} />
          {title}
        </h6>
        <div className="analytics-chart-container">
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

// Line Chart Component
const LineChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;
  
  return (
    <div className="line-chart">
      <svg width="100%" height="200" viewBox="0 0 400 200">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((y, i) => (
          <line
            key={i}
            x1="40"
            y1={40 + y * 1.2}
            x2="360"
            y2={40 + y * 1.2}
            stroke="#f3f4f6"
            strokeWidth="1"
          />
        ))}
        
        {/* Data line */}
        <polyline
          fill="none"
          stroke="var(--primary-color)"
          strokeWidth="3"
          points={data.map((d, i) => {
            const x = 40 + (i * 320) / (data.length - 1);
            const y = 40 + ((maxValue - d.value) / range) * 120;
            return `${x},${y}`;
          }).join(' ')}
        />
        
        {/* Data points */}
        {data.map((d, i) => {
          const x = 40 + (i * 320) / (data.length - 1);
          const y = 40 + ((maxValue - d.value) / range) * 120;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="4"
              fill="var(--primary-color)"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
        
        {/* Labels */}
        {data.map((d, i) => {
          const x = 40 + (i * 320) / (data.length - 1);
          return (
            <text
              key={i}
              x={x}
              y="190"
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {d.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// Bar Chart Component
const BarChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="bar-chart">
      <svg width="100%" height="200" viewBox="0 0 400 200">
        {data.map((d, i) => {
          const barHeight = (d.value / maxValue) * 120;
          const barWidth = 300 / data.length - 10;
          const x = 50 + i * (300 / data.length);
          const y = 160 - barHeight;
          
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill="var(--primary-color)"
                rx="2"
              />
              <text
                x={x + barWidth / 2}
                y={y - 5}
                textAnchor="middle"
                fontSize="10"
                fill="#374151"
              >
                {d.value}
              </text>
              <text
                x={x + barWidth / 2}
                y="185"
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Pie Chart Component
const CustomPieChart = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let currentAngle = 0;
  
  const colors = ['var(--primary-color)', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  
  return (
    <div className="pie-chart">
      <svg width="200" height="200" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="80" fill="#f3f4f6" />
        {data.map((d, i) => {
          const percentage = (d.value / total) * 100;
          const angle = (d.value / total) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          
          const x1 = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
          const y1 = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
          const x2 = 100 + 80 * Math.cos((endAngle * Math.PI) / 180);
          const y2 = 100 + 80 * Math.sin((endAngle * Math.PI) / 180);
          
          const largeArcFlag = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M 100 100`,
            `L ${x1} ${y1}`,
            `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          currentAngle += angle;
          
          return (
            <path
              key={i}
              d={pathData}
              fill={colors[i % colors.length]}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
      </svg>
      <div className="pie-legend">
        {data.map((d, i) => (
          <div key={i} className="legend-item">
            <div 
              className="legend-color" 
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            <span className="legend-label">{d.label}</span>
            <span className="legend-value">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Analytics = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Analytics data
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalHours: 1240.5,
      billableHours: 1080.0,
      nonBillableHours: 160.5,
      totalRevenue: 81000.00,
      averageHourlyRate: 75.00,
      activeProjects: 8,
      completedProjects: 3,
      teamMembers: 12
    },
    trends: {
      hoursThisWeek: 45.5,
      hoursLastWeek: 42.0,
      revenueThisMonth: 25000.00,
      revenueLastMonth: 22000.00,
      productivityScore: 87.5,
      productivityChange: 12.3
    },
    topPerformers: [
      { name: 'Sarah Johnson', hours: 45.5, revenue: 3412.50, efficiency: 95.2 },
      { name: 'Mike Chen', hours: 42.0, revenue: 3150.00, efficiency: 92.8 },
      { name: 'Lisa Wang', hours: 38.5, revenue: 2887.50, efficiency: 89.4 },
      { name: 'John Smith', hours: 35.0, revenue: 2625.00, efficiency: 87.1 }
    ],
    projectBreakdown: [
      { name: 'Digital Transformation', hours: 320.5, revenue: 24037.50, percentage: 25.8 },
      { name: 'Infrastructure Modernization', hours: 280.0, revenue: 21000.00, percentage: 22.6 },
      { name: 'Customer Experience', hours: 245.5, revenue: 18412.50, percentage: 19.8 },
      { name: 'Data Analytics Platform', hours: 200.0, revenue: 15000.00, percentage: 16.1 },
      { name: 'Mobile App Development', hours: 194.5, revenue: 14587.50, percentage: 15.7 }
    ],
    timeDistribution: [
      { category: 'Development', hours: 680.5, percentage: 54.8 },
      { category: 'Design', hours: 245.0, percentage: 19.7 },
      { category: 'Testing', hours: 180.0, percentage: 14.5 },
      { category: 'Meetings', hours: 135.0, percentage: 10.9 }
    ],
    hoursTrend: [
      { label: 'Week 1', value: 35 },
      { label: 'Week 2', value: 42 },
      { label: 'Week 3', value: 38 },
      { label: 'Week 4', value: 45 },
      { label: 'Week 5', value: 48 }
    ],
    timeDistributionPie: [
      { label: 'Development', value: 55 },
      { label: 'Design', value: 20 },
      { label: 'Testing', value: 15 },
      { label: 'Meetings', value: 10 }
    ],
    revenueData: [
      { label: 'Digital Transform', value: 24000 },
      { label: 'Infrastructure', value: 21000 },
      { label: 'Customer Experience', value: 18000 },
      { label: 'Data Analytics', value: 15000 },
      { label: 'Mobile App', value: 12000 }
    ]
  });

  // Load analytics data on component mount
  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, selectedProject, selectedUser]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      // Data is already set in state, just simulate loading
    } catch (error) {
      console.error('Error loading analytics:', error);
      showError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAnalyticsData();
  };

  const handleExport = () => {
    // Simulate export functionality
    success('Analytics data exported successfully');
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const handleProjectChange = (project) => {
    setSelectedProject(project);
  };

  const handleUserChange = (user) => {
    setSelectedUser(user);
  };
  
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh analytics data',
      disabled: loading
    },
    {
      label: '',
      icon: <Download size={18} />,
      onClick: handleExport,
      variant: 'outline',
      tooltip: 'Export analytics report'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Analytics"
        description="Comprehensive insights and analytics for time tracking and productivity"
        actions={headerActions}
      />
      {/* Analytics Page Styles */}
      <style>{`
      /* Analytics Container */
      .analytics-container {
      max-width: 1400px;
      margin: 0 auto;
      }

      .analytics-section {
      margin-bottom: 32px;
      }
    
      .analytics-header-actions {
      display: flex;
      gap: 12px;
      align-items: center;
     }

     .analytics-actions-bar {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-bottom: 24px;
      }

      .analytics-kpi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      gap: 20px;
      }

      .analytics-kpi-card {
      height: 100%;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      transition: box-shadow 0.2s ease;
      }

      .analytics-kpi-card:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      }

      .analytics-kpi-card .card-body {
      padding: 24px;
      }

      .analytics-kpi-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      }

      .analytics-kpi-value {
      font-size: 32px;
      font-weight: 700;
      margin-bottom: 8px;
      }

      .analytics-kpi-label {
      font-size: 14px;
      color: #6b7280;
      margin-bottom: 12px;
      }

      .analytics-kpi-trend {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      font-weight: 500;
      color: #059669;
      }

      .analytics-charts-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      }

      .analytics-chart-card .card {
      height: 100%;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      }

      .analytics-performance-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      }

      .analytics-performance-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      }

      .analytics-table thead th {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      border-bottom: 2px solid #e5e7eb;
      }

      .analytics-user-cell {
      display: flex;
      align-items: center;
      gap: 12px;
      }

      .analytics-user-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background-color: #e3f2fd;
      color: #1976d2;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      }

      .analytics-cards-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      }

      @media (max-width: 1200px) {
      .analytics-charts-grid,
      .analytics-performance-grid {
        grid-template-columns: 1fr;
      }
      }

      @media (max-width: 768px) {
      .analytics-kpi-grid,
      .analytics-cards-grid {
        grid-template-columns: 1fr;
      }
      }
    `}</style>

      <div className="page-container analytics-container">
        
        {/* Filters Section */}
        <section className="analytics-section">
          <div className="analytics-filters-card">
            <div className="analytics-filters-content">
              <div className="analytics-filter-group">
                <label className="analytics-filter-label">Time Range</label>
                <select 
                  className="analytics-filter-select"
                  value={timeRange}
                  onChange={(e) => handleTimeRangeChange(e.target.value)}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
              </div>
              
              <div className="analytics-filter-group">
                <label className="analytics-filter-label">Project</label>
                <select 
                  className="analytics-filter-select"
                  value={selectedProject}
                  onChange={(e) => handleProjectChange(e.target.value)}
                >
                  <option value="all">All Projects</option>
                  <option value="1">Digital Transformation</option>
                  <option value="2">Infrastructure Modernization</option>
                  <option value="3">Customer Experience</option>
                </select>
              </div>
              
              <div className="analytics-filter-group">
                <label className="analytics-filter-label">User</label>
                <select 
                  className="analytics-filter-select"
                  value={selectedUser}
                  onChange={(e) => handleUserChange(e.target.value)}
                >
                  <option value="all">All Users</option>
                  <option value="1">Sarah Johnson</option>
                  <option value="2">Mike Chen</option>
                  <option value="3">Lisa Wang</option>
                </select>
              </div>
              
              <div className="analytics-filter-actions">
                <button 
                  className="analytics-apply-button"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Apply Filters'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Overview Metrics Section */}
        <section className="analytics-section">
          <div className="analytics-kpi-grid">
            <div className="card analytics-kpi-card">
              <div className="card-body">
                <div className="analytics-kpi-content">
                  <div className="analytics-kpi-text">
                    <h3 className="analytics-kpi-value color-1">
                      {analyticsData.overview.totalHours.toFixed(1)}h
                    </h3>
                    <p className="analytics-kpi-label">
                      Total Hours
                    </p>
                    <div className="analytics-kpi-trend">
                      <TrendingUp size={14} />
                      <span>8.5% vs last period</span>
                    </div>
                  </div>
                  <div className="analytics-kpi-icon-container color-1">
                    <Clock size={24} className="analytics-kpi-icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card analytics-kpi-card">
              <div className="card-body">
                <div className="analytics-kpi-content">
                  <div className="analytics-kpi-text">
                    <h3 className="analytics-kpi-value color-2">
                      {analyticsData.overview.billableHours.toFixed(1)}h
                    </h3>
                    <p className="analytics-kpi-label">
                      Billable Hours
                    </p>
                    <div className="analytics-kpi-trend">
                      <TrendingUp size={14} />
                      <span>12.3% vs last period</span>
                    </div>
                  </div>
                  <div className="analytics-kpi-icon-container color-2">
                    <DollarSign size={24} className="analytics-kpi-icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card analytics-kpi-card">
              <div className="card-body">
                <div className="analytics-kpi-content">
                  <div className="analytics-kpi-text">
                    <h3 className="analytics-kpi-value color-3">
                      ${analyticsData.overview.totalRevenue.toLocaleString()}
                    </h3>
                    <p className="analytics-kpi-label">
                      Total Revenue
                    </p>
                    <div className="analytics-kpi-trend">
                      <TrendingUp size={14} />
                      <span>15.7% vs last period</span>
                    </div>
                  </div>
                  <div className="analytics-kpi-icon-container color-3">
                    <DollarIcon size={24} className="analytics-kpi-icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card analytics-kpi-card">
              <div className="card-body">
                <div className="analytics-kpi-content">
                  <div className="analytics-kpi-text">
                    <h3 className="analytics-kpi-value color-5">
                      ${analyticsData.overview.averageHourlyRate.toFixed(0)}
                    </h3>
                    <p className="analytics-kpi-label">
                      Avg Hourly Rate
                    </p>
                    <div className="analytics-kpi-trend">
                      <TrendingDown size={14} />
                      <span>2.1% vs last period</span>
                    </div>
                  </div>
                  <div className="analytics-kpi-icon-container color-5">
                    <TargetIcon size={24} className="analytics-kpi-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Charts Section */}
        <section className="analytics-section">
          <div className="analytics-charts-grid">
            <div className="analytics-chart-card">
              <AnalyticsChart 
                title="Hours Trend Over Time"
                data={analyticsData.hoursTrend}
                type="line"
              />
            </div>
            <div className="analytics-chart-card">
              <AnalyticsChart 
                title="Time Distribution"
                data={analyticsData.timeDistributionPie}
                type="pie"
              />
            </div>
            <div className="analytics-chart-card">
              <AnalyticsChart 
                title="Project Hours Breakdown"
                data={analyticsData.projectBreakdown}
                type="bar"
              />
            </div>
            <div className="analytics-chart-card">
              <AnalyticsChart 
                title="Revenue by Project"
                data={analyticsData.revenueData}
                type="bar"
              />
            </div>
          </div>
        </section>

        {/* Performance Section */}
        <section className="analytics-section">
          <div className="analytics-performance-grid">
            <div className="card analytics-performance-card">
              <div className="card-header">
                <h5 className="card-title mb-0">Top Performers</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm analytics-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Hours</th>
                        <th>Revenue</th>
                        <th>Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.topPerformers.map((performer, index) => (
                        <tr key={index}>
                          <td>
                            <div className="analytics-user-cell">
                              <div className="analytics-user-avatar">
                                {performer.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <span>{performer.name}</span>
                            </div>
                          </td>
                          <td>{performer.hours}h</td>
                          <td>${performer.revenue.toLocaleString()}</td>
                          <td>
                            <span className={`badge analytics-efficiency-badge ${
                              performer.efficiency >= 90 ? 'efficiency-high' : 
                              performer.efficiency >= 80 ? 'efficiency-medium' : 
                              'efficiency-low'
                            }`}>
                              {performer.efficiency}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card analytics-performance-card">
              <div className="card-header">
                <h5 className="card-title mb-0">Productivity Insights</h5>
              </div>
              <div className="card-body">
                <div className="analytics-insights-grid">
                  <div className="analytics-insight-item">
                    <h3 className="analytics-insight-value text-success">
                      {analyticsData.trends.productivityScore}%
                    </h3>
                    <p className="analytics-insight-label">Productivity Score</p>
                    <small className="analytics-insight-trend text-success">
                      <TrendingUp size={14} />
                      +{analyticsData.trends.productivityChange}% vs last period
                    </small>
                  </div>
                  <div className="analytics-insight-item">
                    <h3 className="analytics-insight-value text-primary">
                      {analyticsData.overview.activeProjects}
                    </h3>
                    <p className="analytics-insight-label">Active Projects</p>
                    <small className="analytics-insight-trend text-muted">
                      {analyticsData.overview.completedProjects} completed
                    </small>
                  </div>
                  <div className="analytics-insight-item">
                    <h3 className="analytics-insight-value text-info">
                      {analyticsData.overview.teamMembers}
                    </h3>
                    <p className="analytics-insight-label">Team Members</p>
                  </div>
                  <div className="analytics-insight-item">
                    <h3 className="analytics-insight-value text-warning">
                      {analyticsData.trends.hoursThisWeek}h
                    </h3>
                    <p className="analytics-insight-label">This Week</p>
                    <small className="analytics-insight-trend text-success">
                      <TrendingUp size={14} />
                      +{((analyticsData.trends.hoursThisWeek - analyticsData.trends.hoursLastWeek) / analyticsData.trends.hoursLastWeek * 100).toFixed(1)}% vs last week
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Insights Section */}
        <section className="analytics-section">
          <div className="analytics-cards-grid">
            <div className="card analytics-insight-card">
              <div className="card-body">
                <div className="analytics-insight-icon-wrapper">
                  <div className="analytics-insight-icon color-1">
                    <Activity size={32} />
                  </div>
                </div>
                <h5 className="analytics-insight-card-title">Activity Level</h5>
                <p className="analytics-insight-card-text">
                  High activity detected across all projects with consistent time tracking.
                </p>
              </div>
            </div>

            <div className="card analytics-insight-card">
              <div className="card-body">
                <div className="analytics-insight-icon-wrapper">
                  <div className="analytics-insight-icon color-2">
                    <Award size={32} />
                  </div>
                </div>
                <h5 className="analytics-insight-card-title">Team Performance</h5>
                <p className="analytics-insight-card-text">
                  Excellent team performance with 95%+ efficiency across top performers.
                </p>
              </div>
            </div>

            <div className="card analytics-insight-card">
              <div className="card-body">
                <div className="analytics-insight-icon-wrapper">
                  <div className="analytics-insight-icon color-3">
                    <Zap size={32} />
                  </div>
                </div>
                <h5 className="analytics-insight-card-title">Efficiency Tips</h5>
                <p className="analytics-insight-card-text">
                  Consider optimizing non-billable hours to improve overall productivity.
                </p>
              </div>
            </div>
          </div>
        </section>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default Analytics;